import React, { Fragment } from 'react';
import { Formik, useFormikContext } from 'formik';

import CardDialog from '~/components/CardDialog';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import FsIndexPanel from '~/components/core/FsWrappers/FsIndexPanel/FsIndexPanel';
import {
  useFormikValidationErrorToast,
  ValidationErrorToastContainer,
} from '~/components/hooks/useFormikValidationErrorToast';
import { getInitialValues } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/PhoneConfigurations/PhoneSurveyConfiguration/utils/defaultConfig';
import { handleFormSubmit } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/PhoneConfigurations/PhoneSurveyConfiguration/utils/formSubmission';
import { createSections } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/PhoneConfigurations/PhoneSurveyConfiguration/utils/sections';
import type { AllowSavingState } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/PhoneConfigurations/PhoneSurveyConfiguration/validation/schema';
import { createValidationSchema } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/PhoneConfigurations/PhoneSurveyConfiguration/validation/schema';
import type { PhoneSurveyConfigurationModel } from '~/server_shared/openapi/data-contracts';

const PHONE_SURVEY_CONFIG_TOAST_ID = 'phone-survey-config-errors';

interface EditPhoneSurveyConfigDialogProps {
  configuration?: PhoneSurveyConfigurationModel;
  onCancel: () => void;
  onSubmit: (values: PhoneSurveyConfigurationModel) => Promise<void>;
}

interface EditPhoneSurveyConfigDialogInnerFormikProps {
  configuration?: PhoneSurveyConfigurationModel;
  onCancel: () => void;
  setAllowSavingWithoutNewFiles: React.Dispatch<
    React.SetStateAction<{
      welcome: boolean;
      response_line: boolean;
      illegal: boolean;
      ending: boolean;
      questions: boolean[];
    }>
  >;
}

const EditPhoneSurveyConfigDialogInnerFormik: React.FC<EditPhoneSurveyConfigDialogInnerFormikProps> = ({
  configuration,
  onCancel,
  setAllowSavingWithoutNewFiles,
}) => {
  const { isSubmitting, handleSubmit, values, setFieldValue } = useFormikContext<PhoneSurveyConfigurationModel>();

  useFormikValidationErrorToast({ toastId: PHONE_SURVEY_CONFIG_TOAST_ID });
  const sections = createSections({
    configuration,
    values,
    setFieldValue,
    setAllowSavingWithoutNewFiles,
  });

  return (
    <>
      <ValidationErrorToastContainer containerId={PHONE_SURVEY_CONFIG_TOAST_ID} />
      <CardDialog
        title="Configure Automatic Survey"
        isDialog
        maxWidth="lg"
        fullWidth
        onClose={onCancel}
        preventClose={isSubmitting}
        footerActions={
          <div className="mr-12 mt-8 flex w-full justify-end">
            <DialogFooterActions
              disabled={isSubmitting}
              onClickPrimary={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit();
              }}
              onClickSecondary={isSubmitting ? undefined : onCancel}
              primaryLabel="Save"
              secondaryLabel="Cancel"
            />
          </div>
        }
      >
        <FsIndexPanel
          panelTitle="Survey Configuration"
          sections={sections}
          SectionWrapperComponent={Fragment}
          handleScrollTopOffset={0}
          sectionContainerClassName="ml-[250px]"
          withLeftOffset={false}
        />
      </CardDialog>
    </>
  );
};

const EditPhoneSurveyConfigDialog: React.FC<EditPhoneSurveyConfigDialogProps> = ({
  configuration,
  onCancel,
  onSubmit,
}) => {
  const [allowSavingWithoutNewFiles, setAllowSavingWithoutNewFiles] = React.useState<AllowSavingState>({
    welcome: !!configuration?.welcome_message_configuration?.permanent_audio_file_url,
    response_line: !!configuration?.response_line_configuration?.permanent_audio_file_url,
    illegal: !!configuration?.illegal_response_text_configuration?.permanent_audio_file_url,
    ending: !!configuration?.ending_message_configuration?.permanent_audio_file_url,
    questions: configuration?.questions?.map((q) => !!q?.question_configuration?.permanent_audio_file_url) || [],
  });

  return (
    <Formik
      initialValues={getInitialValues(configuration)}
      validationSchema={createValidationSchema(allowSavingWithoutNewFiles)}
      onSubmit={(values, formikHelpers) => handleFormSubmit(values, formikHelpers, onSubmit, onCancel)}
      enableReinitialize
    >
      <EditPhoneSurveyConfigDialogInnerFormik
        configuration={configuration}
        onCancel={onCancel}
        setAllowSavingWithoutNewFiles={setAllowSavingWithoutNewFiles}
      />
    </Formik>
  );
};

export default EditPhoneSurveyConfigDialog;
